/** SUNTRONIX ONE — Integration Health Centre.
 *
 *  Suntronix-internal admin surface.  Shows the factual health of every
 *  configured integration: production vs sandbox, HEALTHY / ATTENTION /
 *  CRITICAL rollup KPIs, per-row status + last-successful-sync + owner
 *  + retry.  Every KPI opens the filtered list.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/app/PageHeader";
import Section from "@/components/app/Section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  RefreshCcw, ShieldAlert, PlugZap, Activity, AlertTriangle, CheckCircle2,
  Clock, KeyRound, RotateCw, Wrench, HeartPulse, Plus,
} from "lucide-react";

const HEALTH_TONE = {
  healthy:              "text-emerald-700 bg-emerald-50 border-emerald-200",
  attention:            "text-amber-700 bg-amber-50 border-amber-200",
  critical:             "text-red-700 bg-red-50 border-red-200",
  manual_no_automation: "text-slate-700 bg-slate-100 border-slate-200",
  not_configured:       "text-slate-500 bg-slate-50 border-slate-200",
};

const HEALTH_ICON = {
  healthy: CheckCircle2, attention: AlertTriangle, critical: PlugZap,
  manual_no_automation: Wrench, not_configured: PlugZap,
};

function HealthChip({ level }) {
  const I = HEALTH_ICON[level] || Activity;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded border ${HEALTH_TONE[level] || HEALTH_TONE.not_configured}`}>
      <I className="h-3 w-3" /> {level.replace(/_/g, " ")}
    </span>
  );
}

function since(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} h ago`;
  return `${Math.floor(hrs / 24)} d ago`;
}

export default function IntegrationHealth() {
  const { user } = useAuth();
  const isSuntronix = user && !user.master_company_id;
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ health_level: "", category: "" });
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [regForm, setRegForm] = useState({
    name: "", provider: "", category: "jamf", environment: "production",
    connection_method: "api", authentication_type: "oauth2",
    direction: "inbound", data_domains: "",
    expected_frequency_min: 15, warning_threshold_min: 30,
    critical_threshold_min: 120, credential_warn_days: 30,
    credential_expires_at: "", operational_owner_email: "",
    configured: true, manual_mode: false,
    manual_fallback_available: false, fallback_description: "",
  });
  const [regBusy, setRegBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: sm }] = await Promise.all([
        api.get("/integration-health", { params: filter }),
        api.get("/integration-health/summary"),
      ]);
      setRows(list.items || []);
      setSummary(sm);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load integration health");
    } finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { if (isSuntronix) load(); }, [isSuntronix, load]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter((r) =>
      `${r.name} ${r.provider} ${r.category} ${r.master_company_id || ""}`
        .toLowerCase().includes(query));
  }, [rows, q]);

  const openDetail = async (row) => {
    setSelected(row);
    try {
      const { data } = await api.get(`/integration-health/${row.id}`);
      setDetail(data);
    } catch { setDetail(null); }
  };

  const retry = async (row) => {
    try {
      await api.post(`/integration-health/${row.id}/retry`,
        { operation: "sync", reason: "manual retry" });
      toast.success("Retry queued");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Retry failed");
    }
  };

  const testConn = async (row) => {
    try {
      const { data } = await api.post(`/integration-health/${row.id}/test-connection`);
      toast.success(`Test result: ${data.computed?.status || "done"}`);
      await load();
      if (selected?.id === row.id) openDetail(row);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    }
  };

  if (!isSuntronix) {
    return (
      <div className="max-w-lg mx-auto mt-16 st-panel p-6 text-center"
            data-testid="ih-forbidden">
        <ShieldAlert className="h-8 w-8 mx-auto text-slate-400 mb-2" />
        <div className="font-heading text-lg mb-1">Suntronix internal only</div>
        <div className="text-sm text-slate-500">
          The Integration Health Centre is restricted to Suntronix administrators.
        </div>
      </div>
    );
  }

  const kpis = summary ? [
    { key: "healthy",    label: "Healthy",          n: summary.by_health?.healthy || 0,             tone: "text-emerald-700", filter: { health_level: "healthy" } },
    { key: "attention",  label: "Attention",        n: summary.by_health?.attention || 0,           tone: "text-amber-700",   filter: { health_level: "attention" } },
    { key: "critical",   label: "Critical",         n: summary.by_health?.critical || 0,            tone: "text-red-700",     filter: { health_level: "critical" } },
    { key: "credexp",    label: "Credentials Expiring", n: summary.credentials_expiring,             tone: "text-amber-700",   filter: {} },
    { key: "manual",     label: "Manual",           n: summary.manual,                              tone: "text-slate-700",   filter: { health_level: "manual_no_automation" } },
    { key: "notconf",    label: "Not configured",   n: summary.not_configured,                      tone: "text-slate-500",   filter: { health_level: "not_configured" } },
  ] : [];

  return (
    <div className="space-y-6 max-w-[1400px]" data-testid="integration-health-page">
      <PageHeader
        eyebrow="Administration · Integrations"
        title="Integration Health"
        context="Connection status, data freshness and operational impact across SUNTRONIX ONE."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={load} disabled={loading}
              data-testid="ih-refresh">
              <RefreshCcw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button variant="create" onClick={() => setRegisterOpen(true)}
              data-testid="ih-register">
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Register integration
            </Button>
          </div>
        }
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {kpis.map((k) => (
          <button key={k.key}
             onClick={() => setFilter(k.filter)}
             className={`st-panel p-3 text-left border transition
              ${filter.health_level === (k.filter.health_level || "") ? "border-slate-900" : "border-slate-200"}`}
             data-testid={`ih-kpi-${k.key}`}>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500">
              <HeartPulse className="h-3 w-3" /> {k.label}
            </div>
            <div className={`text-2xl font-heading font-semibold mt-1 tabular-nums ${k.tone}`}>
              {k.n}
            </div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Filter by name / provider / customer…"
          className="max-w-sm h-9 text-[13px]"
          data-testid="ih-search" />
        <select className="border rounded h-9 px-2 text-[12px]"
          value={filter.category || ""}
          onChange={(e) => setFilter((f) => ({ ...f, category: e.target.value }))}
          data-testid="ih-category-filter">
          <option value="">All categories</option>
          {["abm","ade","jamf","intune","autopilot","entra_id","hris","erp",
            "courier","email","sms","fx","webhook","api"].map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select className="border rounded h-9 px-2 text-[12px]"
          value={filter.health_level || ""}
          onChange={(e) => setFilter((f) => ({ ...f, health_level: e.target.value }))}
          data-testid="ih-health-filter">
          <option value="">All health</option>
          <option value="healthy">Healthy</option>
          <option value="attention">Attention</option>
          <option value="critical">Critical</option>
          <option value="manual_no_automation">Manual</option>
          <option value="not_configured">Not configured</option>
        </select>
        <div className="text-[11px] text-slate-500 ml-auto">
          {filtered.length} of {rows.length} shown
        </div>
      </div>

      {/* Table */}
      <Section title="Integrations" testId="ih-table">
        <div className="st-panel overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Integration</th>
                <th className="text-left px-3 py-2">Category</th>
                <th className="text-left px-3 py-2">Environment</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Last successful op</th>
                <th className="text-left px-3 py-2">Owner</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && rows.length === 0 && (
                <tr><td colSpan={7} className="text-center text-slate-400 text-[12px] py-8">Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={7} className="text-center text-slate-400 text-[12px] py-8"
                          data-testid="ih-empty">
                  No integrations match. Register one from the API to get started.
                </td></tr>
              )}
              {filtered.map((r) => {
                const c = r.computed || {};
                return (
                  <tr key={r.id}
                        className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                        data-testid={`ih-row-${r.id}`}
                        onClick={() => openDetail(r)}>
                    <td className="px-3 py-2">
                      <div className="font-medium">{r.name}</div>
                      <div className="text-[10px] text-slate-500">{r.provider}</div>
                    </td>
                    <td className="px-3 py-2 text-[12px] uppercase text-slate-600">{r.category}</td>
                    <td className="px-3 py-2 text-[12px]">
                      <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border
                        ${r.environment === "production" ? "border-slate-900 text-slate-900"
                          : "border-slate-300 text-slate-600"}`}>
                        {r.environment}
                      </span>
                    </td>
                    <td className="px-3 py-2 space-y-0.5">
                      <HealthChip level={c.health_level} />
                      <div className="text-[10px] text-slate-500">{c.status}</div>
                    </td>
                    <td className="px-3 py-2 text-[12px] text-slate-600">
                      <div>{since(r.last_successful_op_at)}</div>
                      {c.freshness === "stale_critical" && (
                        <div className="text-[10px] text-red-600">DATA STALE</div>
                      )}
                      {c.freshness === "stale_warning" && (
                        <div className="text-[10px] text-amber-600">Approaching stale</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[12px] text-slate-700">
                      {r.operational_owner_email || r.configuration_owner_email || "—"}
                    </td>
                    <td className="px-3 py-2 text-right"
                          onClick={(e) => e.stopPropagation()}>
                      <div className="inline-flex gap-1">
                        <Button size="sm" variant="outline"
                          onClick={() => testConn(r)}
                          data-testid={`ih-test-${r.id}`}>
                          <Activity className="h-3 w-3 mr-1" /> Test
                        </Button>
                        <Button size="sm" variant="outline"
                          onClick={() => retry(r)}
                          data-testid={`ih-retry-${r.id}`}>
                          <RotateCw className="h-3 w-3 mr-1" /> Retry
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Section>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => { if (!v) { setSelected(null); setDetail(null); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto"
          data-testid="ih-detail-dialog">
          <DialogHeader>
            <DialogTitle>{selected?.name}</DialogTitle>
            <div className="text-[12px] text-slate-500">
              {selected?.provider} · {selected?.category} · {selected?.environment}
            </div>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Field label="Health">
                  <HealthChip level={detail.item?.computed?.health_level} />
                </Field>
                <Field label="Status">{detail.item?.computed?.status}</Field>
                <Field label="Freshness">{detail.item?.computed?.freshness}</Field>
                <Field label="Credential">{detail.item?.computed?.credential}</Field>
                <Field label="Last successful op">{since(detail.item?.last_successful_op_at)}</Field>
                <Field label="Last successful connection">{since(detail.item?.last_successful_connection_at)}</Field>
                <Field label="Last failure">{since(detail.item?.last_failed_at)}</Field>
                <Field label="Records processed" mono>{detail.item?.records_processed ?? 0}</Field>
              </div>

              {detail.item?.computed?.notes?.length > 0 && (
                <div className="rounded border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900">
                  {detail.item.computed.notes.map((n, i) => <div key={i}>• {n}</div>)}
                </div>
              )}

              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Recent events</div>
                <div className="max-h-64 overflow-y-auto border rounded">
                  <table className="w-full text-[12px]">
                    <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-1">When</th>
                        <th className="text-left px-2 py-1">Op</th>
                        <th className="text-left px-2 py-1">Result</th>
                        <th className="text-left px-2 py-1">Error</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(detail.recent_events || []).map((e) => (
                        <tr key={e.id} className="border-t border-slate-100">
                          <td className="px-2 py-1 text-[11px]">{since(e.at)}</td>
                          <td className="px-2 py-1">{e.operation}</td>
                          <td className={`px-2 py-1 uppercase text-[10px] font-semibold ${
                            e.result === "success" ? "text-emerald-700"
                            : e.result === "failure" ? "text-red-700" : "text-slate-500"}`}>
                            {e.result}
                          </td>
                          <td className="px-2 py-1 text-[11px] text-slate-600">
                            {e.error_code || e.error_message || ""}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => selected && testConn(selected)}
              data-testid="ih-detail-test">
              <Activity className="h-3.5 w-3.5 mr-1" /> Test connection
            </Button>
            <Button onClick={() => selected && retry(selected)}
              data-testid="ih-detail-retry">
              <RotateCw className="h-3.5 w-3.5 mr-1" /> Retry sync
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Register dialog */}
      <Dialog open={registerOpen} onOpenChange={setRegisterOpen}>
        <DialogContent className="max-w-2xl" data-testid="ih-register-dialog">
          <DialogHeader>
            <DialogTitle>Register a new integration</DialogTitle>
            <div className="text-[12px] text-slate-500">
              Once registered, the integration will send heartbeats via the API. Freshness thresholds and credential expiry drive the computed health level automatically.
            </div>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <RegField label="Name (required)">
              <Input value={regForm.name}
                onChange={(e) => setRegForm({ ...regForm, name: e.target.value })}
                placeholder="Jamf Pro — ABC Global"
                data-testid="ih-reg-name" />
            </RegField>
            <RegField label="Provider (required)">
              <Input value={regForm.provider}
                onChange={(e) => setRegForm({ ...regForm, provider: e.target.value })}
                placeholder="Jamf"
                data-testid="ih-reg-provider" />
            </RegField>
            <RegField label="Category">
              <select className="w-full border rounded h-9 px-2 text-[13px]"
                value={regForm.category}
                onChange={(e) => setRegForm({ ...regForm, category: e.target.value })}
                data-testid="ih-reg-category">
                {["abm","ade","jamf","intune","autopilot","entra_id","soti",
                  "other_mdm","hris","erp","finance","procurement","courier",
                  "email","sms","document_storage","sso","scim","api",
                  "webhook","fx","customer_specific","partner"].map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </RegField>
            <RegField label="Environment">
              <select className="w-full border rounded h-9 px-2 text-[13px]"
                value={regForm.environment}
                onChange={(e) => setRegForm({ ...regForm, environment: e.target.value })}
                data-testid="ih-reg-env">
                {["production","uat","sandbox","test","manual"].map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </RegField>
            <RegField label="Direction">
              <select className="w-full border rounded h-9 px-2 text-[13px]"
                value={regForm.direction}
                onChange={(e) => setRegForm({ ...regForm, direction: e.target.value })}>
                {["inbound","outbound","bidirectional"].map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </RegField>
            <RegField label="Data domains (comma-separated)">
              <Input value={regForm.data_domains}
                onChange={(e) => setRegForm({ ...regForm, data_domains: e.target.value })}
                placeholder="assets, mdm, deployments" />
            </RegField>
            <RegField label="Expected freq. (min)">
              <Input type="number" value={regForm.expected_frequency_min}
                onChange={(e) => setRegForm({ ...regForm, expected_frequency_min: Number(e.target.value) })} />
            </RegField>
            <RegField label="Warning threshold (min)">
              <Input type="number" value={regForm.warning_threshold_min}
                onChange={(e) => setRegForm({ ...regForm, warning_threshold_min: Number(e.target.value) })} />
            </RegField>
            <RegField label="Critical threshold (min)">
              <Input type="number" value={regForm.critical_threshold_min}
                onChange={(e) => setRegForm({ ...regForm, critical_threshold_min: Number(e.target.value) })} />
            </RegField>
            <RegField label="Credential expiry (ISO — optional)">
              <Input value={regForm.credential_expires_at}
                onChange={(e) => setRegForm({ ...regForm, credential_expires_at: e.target.value })}
                placeholder="2026-06-01T00:00:00Z" />
            </RegField>
            <RegField label="Credential warn (days)">
              <Input type="number" value={regForm.credential_warn_days}
                onChange={(e) => setRegForm({ ...regForm, credential_warn_days: Number(e.target.value) })} />
            </RegField>
            <RegField label="Operational owner email">
              <Input value={regForm.operational_owner_email}
                onChange={(e) => setRegForm({ ...regForm, operational_owner_email: e.target.value })}
                placeholder="dm@suntronix.net" />
            </RegField>
            <RegField label="Manual fallback">
              <label className="flex items-center gap-2 text-[12px] mt-2">
                <input type="checkbox"
                  checked={regForm.manual_fallback_available}
                  onChange={(e) => setRegForm({ ...regForm, manual_fallback_available: e.target.checked })} />
                Available
              </label>
            </RegField>
            <RegField label="Fallback description">
              <Input value={regForm.fallback_description}
                onChange={(e) => setRegForm({ ...regForm, fallback_description: e.target.value })}
                placeholder="Manual MDM verification allowed for critical deployments" />
            </RegField>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRegisterOpen(false)} disabled={regBusy}>Cancel</Button>
            <Button
              onClick={async () => {
                if (!regForm.name.trim() || !regForm.provider.trim()) {
                  toast.error("Name and provider are required."); return;
                }
                setRegBusy(true);
                try {
                  const payload = {
                    ...regForm,
                    data_domains: regForm.data_domains
                      ? regForm.data_domains.split(",").map((s) => s.trim()).filter(Boolean)
                      : [],
                    credential_expires_at: regForm.credential_expires_at.trim() || null,
                  };
                  await api.post("/integration-health", payload);
                  toast.success(`${regForm.name} registered`);
                  setRegisterOpen(false);
                  setRegForm({ ...regForm, name: "", provider: "" });
                  load();
                } catch (e) {
                  toast.error(e.response?.data?.detail || "Register failed");
                } finally { setRegBusy(false); }
              }}
              disabled={regBusy}
              data-testid="ih-reg-submit">
              <Plus className="h-3.5 w-3.5 mr-1" /> Register
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RegField({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

function Field({ label, children, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-[13px] ${mono ? "font-mono" : ""} text-slate-800`}>
        {children ?? "—"}
      </div>
    </div>
  );
}
